// src/pages/Admin/Support/AdminTicketDetails.jsx
import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaUser, 
  FaCalendar, 
  FaPaperPlane, 
  FaCheckCircle,
  FaTicketAlt,
  FaComments 
} from 'react-icons/fa';
import Sidebar from '../../../components/Common/Sidebar';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const AdminTicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.tickets.getDetails(ticketId);
      setTicket(response.data.data.ticket);
      setNewStatus(response.data.data.ticket.status);
    } catch (error) {
      toast.error('Failed to fetch ticket details');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (replyMessage.length < 10) {
      toast.error('Reply must be at least 10 characters');
      return;
    }

    try {
      setSubmitting(true);
      await adminAPI.tickets.reply(ticketId, { message: replyMessage });
      
      toast.success('Reply sent successfully');
      setReplyMessage('');
      
      // Refresh ticket details
      await fetchTicketDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (ticket.status === status) {
      return; // No change needed
    }

    try {
      await adminAPI.tickets.updateStatus(ticketId, { status });
      toast.success('Ticket status updated successfully');
      await fetchTicketDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'primary',
      in_progress: 'info',
      resolved: 'success',
      closed: 'secondary'
    };
    return variants[status] || 'secondary';
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'secondary',
      medium: 'warning',
      high: 'danger',
      urgent: 'danger'
    };
    return variants[priority] || 'secondary';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="d-flex">
        <Sidebar role="admin" />
        <div className="flex-grow-1 p-4">
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--accent-cyan)' }} />
            <p className="mt-3">Loading ticket details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="d-flex">
        <Sidebar role="admin" />
        <div className="flex-grow-1 p-4">
          <div className="text-center py-5">
            <h5>Ticket not found</h5>
            <Button variant="primary" onClick={() => navigate('/admin/support')}>
              Back to Support Tickets
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
  <Sidebar role="admin" />
  <div className="dashboard-content">

        <div className="mb-4">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={() => navigate('/admin/support')}
            className="mb-3"
          >
            <FaArrowLeft className="me-2" />
            Back to Support Tickets
          </Button>
          
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="fw-bold mb-1">
                <FaTicketAlt className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                Ticket #{ticket.ticket_id}
              </h2>
              <p className="text-muted mb-0">{ticket.subject}</p>
            </div>
            
            <div className="d-flex gap-2">
              {ticket.status !== 'closed' && (
                <>
                  {ticket.status !== 'in_progress' && (
                    <Button 
                      variant="info" 
                      size="sm"
                      onClick={() => handleStatusUpdate('in_progress')}
                    >
                      Mark In Progress
                    </Button>
                  )}
                  {ticket.status !== 'resolved' && (
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => handleStatusUpdate('resolved')}
                    >
                      <FaCheckCircle className="me-2" />
                      Mark Resolved
                    </Button>
                  )}
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => handleStatusUpdate('closed')}
                  >
                    Close Ticket
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Ticket Details & Conversation */}
          <div className="col-md-8">
            {/* Ticket Info Card */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="fw-bold mb-2">{ticket.subject}</h5>
                    <div className="d-flex gap-2">
                      <Badge bg={getStatusBadge(ticket.status)}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <Badge bg={getPriorityBadge(ticket.priority)}>
                        {ticket.priority.toUpperCase()} PRIORITY
                      </Badge>
                    </div>
                  </div>
                  <div className="text-end">
                    <small className="text-muted d-block">
                      <FaCalendar className="me-1" />
                      Created: {formatDate(ticket.created_at)}
                    </small>
                    {ticket.resolved_at && (
                      <small className="text-success d-block mt-1">
                        <FaCheckCircle className="me-1" />
                        Resolved: {formatDate(ticket.resolved_at)}
                      </small>
                    )}
                  </div>
                </div>
                
                <div className="border-top pt-3">
                  <strong className="d-block mb-2">Description:</strong>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    {ticket.description}
                  </p>
                </div>
              </Card.Body>
            </Card>

            {/* Conversation */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaComments className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  Conversation ({ticket.replies?.length || 0})
                </h5>
              </Card.Header>
              <Card.Body>
                {ticket.replies && ticket.replies.length > 0 ? (
                  <div className="conversation-container">
                    {ticket.replies.map((reply, index) => (
                      <div 
                        key={index}
                        className={`message-card ${reply.is_admin_reply ? 'admin-message' : 'user-message'}`}
                      >
                        <div className="message-header">
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className="avatar"
                              style={{
                                backgroundColor: reply.is_admin_reply ? 'var(--accent-cyan)' : '#6c757d'
                              }}
                            >
                              {reply.is_admin_reply ? 'A' : 'U'}
                            </div>
                            <div>
                              <strong className={reply.is_admin_reply ? 'text-primary' : ''}>
                                {reply.is_admin_reply ? '👨‍💼 Support Team (You)' : `👤 ${ticket.user?.username}`}
                              </strong>
                              <small className="text-muted d-block">
                                {formatDate(reply.created_at)}
                              </small>
                            </div>
                          </div>
                        </div>
                        <div className="message-body">
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            {reply.message}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info">
                    <FaComments className="me-2" />
                    No replies yet. Be the first to respond to this ticket.
                  </Alert>
                )}

                {/* Reply Form */}
                {ticket.status !== 'closed' ? (
                  <div className="mt-4">
                    <Form onSubmit={handleSendReply}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaPaperPlane className="me-2" />
                          Send Reply to User
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          placeholder="Type your response here..."
                          disabled={submitting}
                        />
                        <Form.Text className="text-muted">
                          Minimum 10 characters required
                        </Form.Text>
                      </Form.Group>
                      
                      <div className="d-flex gap-2">
                        <Button 
                          type="submit"
                          variant="primary"
                          disabled={submitting || replyMessage.length < 10}
                          className="px-4"
                        >
                          {submitting ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane className="me-2" />
                              Send Reply
                            </>
                          )}
                        </Button>
                        
                        {ticket.status === 'open' && (
                          <Button 
                            variant="outline-info"
                            onClick={() => handleStatusUpdate('in_progress')}
                          >
                            Mark In Progress
                          </Button>
                        )}
                      </div>
                    </Form>
                  </div>
                ) : (
                  <Alert variant="secondary" className="mt-4 mb-0">
                    <FaCheckCircle className="me-2" />
                    This ticket is closed. No further replies can be added.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* User Information Sidebar */}
          <div className="col-md-4">
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header className="bg-white border-bottom">
                <h5 className="mb-0 fw-bold">
                  <FaUser className="me-2" style={{ color: 'var(--accent-cyan)' }} />
                  User Information
                </h5>
              </Card.Header>
              <Card.Body>
                {ticket.user ? (
                  <>
                    <div className="text-center mb-3">
                      <div 
                        className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                        style={{ 
                          width: '80px', 
                          height: '80px', 
                          backgroundColor: 'var(--accent-cyan)',
                          fontSize: '32px',
                          fontWeight: 'bold',
                          color: 'var(--bg-dark)'
                        }}
                      >
                        {ticket.user.username?.charAt(0).toUpperCase()}
                      </div>
                      <h5 className="fw-bold mb-1">{ticket.user.username}</h5>
                      <p className="text-muted mb-0 small">{ticket.user.email}</p>
                    </div>

                    <hr />

                    <table className="table table-borderless mb-0 small">
                      <tbody>
                        <tr>
                          <td className="text-muted">User ID:</td>
                          <td className="text-end fw-bold">#{ticket.user.id}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Role:</td>
                          <td className="text-end">
                            <Badge bg="info">{ticket.user.user_role}</Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status:</td>
                          <td className="text-end">
                            <Badge bg={ticket.user.status === 'approved' ? 'success' : 'warning'}>
                              {ticket.user.status}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Account:</td>
                          <td className="text-end">
                            <Badge bg={ticket.user.account_status === 'active' ? 'success' : 'secondary'}>
                              {ticket.user.account_status || 'inactive'}
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="d-grid gap-2 mt-3">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => navigate(`/admin/users/${ticket.user.id}`)}
                      >
                        View User Profile
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-muted text-center mb-0">User information not available</p>
                )}
              </Card.Body>
            </Card>

            {/* Quick Actions */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <h6 className="mb-0 fw-bold">Quick Actions</h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  {ticket.status === 'open' && (
                    <Button 
                      variant="info"
                      size="sm"
                      onClick={() => handleStatusUpdate('in_progress')}
                    >
                      Mark In Progress
                    </Button>
                  )}
                  
                  {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                    <Button 
                      variant="success"
                      size="sm"
                      onClick={() => handleStatusUpdate('resolved')}
                    >
                      <FaCheckCircle className="me-2" />
                      Mark as Resolved
                    </Button>
                  )}
                  
                  {ticket.status !== 'closed' && (
                    <Button 
                      variant="secondary"
                      size="sm"
                      onClick={() => handleStatusUpdate('closed')}
                    >
                      Close Ticket
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => navigate('/admin/support')}
                  >
                    Back to List
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      <style jsx>{`
        .conversation-container {
          max-height: 500px;
          overflow-y: auto;
          padding-right: 10px;
        }

        .conversation-container::-webkit-scrollbar {
          width: 6px;
        }

        .conversation-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        .conversation-container::-webkit-scrollbar-thumb {
          background: var(--accent-cyan);
          border-radius: 10px;
        }

        .message-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 15px;
          border: 1px solid #e9ecef;
          transition: all 0.2s ease;
        }

        .message-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .message-card.admin-message {
          background: rgba(118, 234, 215, 0.05);
          border-left: 4px solid var(--accent-cyan);
        }

        .message-card.user-message {
          background: white;
          border-left: 4px solid #dee2e6;
        }

        .message-header {
          margin-bottom: 10px;
        }

        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 18px;
        }

        .message-body {
          padding-left: 50px;
        }

        .message-body p {
          color: #495057;
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
};

export default AdminTicketDetails;